import sodium from 'libsodium-wrappers-sumo';

// Base64 helper (UrlSafe, no padding for standard use, or standard base64)
const toBase64 = (bytes) => {
  const array = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (let index = 0; index < array.length; index += 1) {
    binary += String.fromCharCode(array[index]);
  }
  return btoa(binary);
};
const fromBase64 = (str) => {
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};
const encodeUtf8 = (str) => new TextEncoder().encode(str);
const decodeUtf8 = (buffer) => new TextDecoder().decode(buffer);

export const WebCryptoProvider = {
  async deriveKeyArgon2id(password, salt, iterations, memory, parallelism, hashLength) {
    await sodium.ready;
    const key = sodium.crypto_pwhash(
      hashLength,
      encodeUtf8(password),
      sodium.crypto_generichash(sodium.crypto_pwhash_SALTBYTES, encodeUtf8(salt)), // Ensure salt is exact size
      iterations,
      memory * 1024, // sodium takes bytes, we want KB
      sodium.crypto_pwhash_ALG_ARGON2ID13
    );
    return toBase64(key);
  },

  async encryptAESGCM(keyBase64, ivBase64, plaintext, aad) {
    const key = await crypto.subtle.importKey(
      'raw',
      fromBase64(keyBase64),
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );
    
    const iv = fromBase64(ivBase64);
    const data = encodeUtf8(plaintext);
    const additionalData = aad ? encodeUtf8(aad) : new Uint8Array(0);

    const ciphertextBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
        additionalData
      },
      key,
      data
    );

    // Web Crypto API appends the 16-byte auth tag at the end of the ciphertext
    const ciphertextBytes = new Uint8Array(ciphertextBuffer);
    const authTagBytes = ciphertextBytes.slice(-16);
    const actualCiphertext = ciphertextBytes.slice(0, -16);

    return {
      ciphertext: toBase64(actualCiphertext),
      authTag: toBase64(authTagBytes)
    };
  },

  async decryptAESGCM(keyBase64, ivBase64, ciphertextBase64, authTagBase64, aad) {
    const key = await crypto.subtle.importKey(
      'raw',
      fromBase64(keyBase64),
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    const iv = fromBase64(ivBase64);
    const ctBytes = fromBase64(ciphertextBase64);
    const macBytes = fromBase64(authTagBase64);
    const additionalData = aad ? encodeUtf8(aad) : new Uint8Array(0);

    // Reconstruct the buffer that Web Crypto API expects (ciphertext + mac)
    const combined = new Uint8Array(ctBytes.length + macBytes.length);
    combined.set(ctBytes);
    combined.set(macBytes, ctBytes.length);

    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
        additionalData
      },
      key,
      combined
    );

    return decodeUtf8(decryptedBuffer);
  },

  randomBytes(length) {
    const arr = new Uint8Array(length);
    crypto.getRandomValues(arr);
    return toBase64(arr);
  }
};
